import { storeToRefs } from "pinia";
import { ChecklistCriteria, CriteriaInstance } from "types/Checklist";

export const useChecklistController = (documentId: string) =>
  defineStore(`checklist-controller/${documentId}`, () => {
    const documentViewModeStore = useDocumentViewMode(documentId)();
    const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

    const submittedDocumentStore = useSubmittedDocumentStore(documentId);
    const { hasChecklist } = storeToRefs(submittedDocumentStore);

    const documentChecklistStore = useDocumentChecklistStore(documentId);
    const { useChecklist } = storeToRefs(documentChecklistStore);

    const checklistControllerMode = ref<ChecklistControllerMode>(
      ChecklistControllerMode.reviewSteps
    );

    const currentStepIndex = ref<number>(-1);
    const currentChecklistCriteriaInstance = ref<
      CriteriaInstance | undefined
    >();

    const getCriteriaFromId = (id: string): ChecklistCriteria | undefined => {
      const { getCriteriaFromId } = useDocumentChecklistStore(documentId);
      return getCriteriaFromId(id);
    };

    const initializeController = () => {
      if (isTeacherViewMode.value == true || hasChecklist.value != true) {
        checklistControllerMode.value = ChecklistControllerMode.summary;
      } else {
        if (useChecklist.value?.hasBeenReviewedByStudent == true) {
          checklistControllerMode.value = ChecklistControllerMode.summary;
        } else {
          checklistControllerMode.value = ChecklistControllerMode.reviewSteps;
          currentStepIndex.value = useChecklist.value?.lastViewedStep ?? 0;
        }
      }
    };

    const useAllChecklistCriteria = computed(() => {
      const store = useDocumentChecklistStore(documentId);
      const { useAllChecklistCriteria } = storeToRefs(store);
      return useAllChecklistCriteria.value ?? [];
    });

    const useAllCriteriaInstances = computed(() => {
      const store = useDocumentChecklistStore(documentId);
      const { useAllCriteriaInstances } = storeToRefs(store);
      return useAllCriteriaInstances.value ?? [];
    });

    const numSteps = computed(() => {
      return useAllCriteriaInstances.value.length ?? 0;
    });

    const nextStep = () => {
      if (currentStepIndex.value >= numSteps.value) {
        checklistControllerMode.value = ChecklistControllerMode.summary;
        documentChecklistStore.setHasBeenReviewedByStudent();

        currentStepIndex.value = -1;
        setCurrentChecklistCriteriaInstance(undefined);
        return;
      }

      if (currentStepIndex.value < numSteps.value) {
        currentStepIndex.value = currentStepIndex.value + 1;
      }
    };

    const previousStep = () => {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value--;
      }
    };

    watch(currentStepIndex, (newVal) => {
      if (currentStepIndex.value >= 0) {
        documentChecklistStore.setLastViewedStep(newVal);
      }
    });

    const currentStep = computed((): CriteriaInstance | undefined => {
      return useAllCriteriaInstances.value[currentStepIndex.value - 1];
    });

    const currentCriteria = computed(() => {
      if (
        currentChecklistCriteriaInstance.value?.checklistCriteriaId == undefined
      )
        return undefined;

      return getCriteriaFromId(
        currentChecklistCriteriaInstance.value?.checklistCriteriaId
      );
    });

    const setCurrentChecklistCriteriaInstance = (
      criteriaInstance: CriteriaInstance | undefined
    ) => {
      currentChecklistCriteriaInstance.value = criteriaInstance;
    };

    const savedScrollPosition = ref<number | undefined>(undefined);

    const setScrollPosition = (position: number | undefined) => {
      savedScrollPosition.value = position;
    };

    return {
      initializeController,
      nextStep,
      previousStep,
      getCriteriaFromId,
      setCurrentChecklistCriteriaInstance,
      setScrollPosition,
      currentStep,
      currentChecklistCriteriaInstance,
      currentCriteria,
      currentStepIndex,
      numSteps,
      savedScrollPosition,
      checklistControllerMode,
    };
  });
